import { v4 as uuidv4 } from 'uuid';
import { DataNode } from 'antd/es/tree';
import { DescriptionAnswer, LinkAnswer, Question, ReactFlowEdge, ReactFlowNode, StepsAnswer, YesNoAnswer } from '../types/common';

export const getConnectedChildNodes = (sourceId: string, edges: any[] = []) => {
    const connectedNodes = new Set<string>();
    const stack = edges?.filter(edge => edge.source === sourceId).map(edge => edge.target).filter(Boolean);

    while (stack.length) {
        const currentNode = stack.pop();
        if (connectedNodes.has(currentNode!)) continue;

        connectedNodes.add(currentNode!);

        const targets = edges?.filter(edge => edge.source === currentNode)
            .map(edge => edge.target)
            .filter(Boolean);

        stack.push(...targets);
    }

    return Array.from(connectedNodes);
};

export const getConnectedNodesWithSource = (nodeId: string, edges: any[]) => {
    const connectedNodes = new Set<string>();
    const stack = [nodeId];

    while (stack.length) {
        const currentNode = stack.pop();
        if (connectedNodes.has(currentNode!)) continue;

        connectedNodes.add(currentNode!);

        // Find all target nodes connected to the current node
        const targets = edges?.filter(edge => edge.source === currentNode)
            .map(edge => edge.target);

        stack.push(...targets);
    }

    return Array.from(connectedNodes);
};

export const hasInvalidData = (nodes: any) => {
    if (!Array.isArray(nodes) || nodes.length === 0) return true;

    return nodes.some((node) => {
        if (['questionNode', 'descriptionNode', 'stepsNode', 'linkNode'].includes(node?.type)) {
            if (!node?.data?.content?.trim()) {
                console.error("Empty value found in", node?.data.content);
                return true;
            }
        }
        return false;
    });
};

/* Node Creation */
const ids = {
    question: uuidv4(),
    addButton: uuidv4(),
    description: uuidv4(),
    uploadButton: uuidv4(),
    upload: uuidv4(),
    steps: uuidv4(),
    yes: uuidv4(),
    no: uuidv4()
};

export const InitialNodesEdgesByType: any = {
    "paragraph": {
        nodes: [
            {
                id: ids.question,
                type: 'questionNode',
                data: { content: '', onContentUpdate: null, label: "parentNode" },
            },
            {
                id: ids.addButton,
                type: 'addButtonNode',
                data: { label: "addButtonNode", buttonType: "paragraph" },
            },
            {
                id: ids.description,
                type: 'descriptionNode',
                data: { label: "descriptionNode" },
            },
            {
                id: ids.uploadButton,
                type: 'uploadButtonNode',
                data: { label: "uploadButtonNode", buttonType: "upload" },
            },
            {
                id: ids.upload,
                type: 'uploadNode',
                data: { label: "uploadNode" },
            }
        ],
        edges: [
            {
                id: `${ids.question}-${ids.addButton}`,
                source: ids.question,
                target: ids.addButton,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.addButton}-${ids.description}`,
                source: ids.addButton,
                target: ids.description,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.description}-${ids.uploadButton}`,
                source: ids.description,
                target: ids.uploadButton,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.uploadButton}-${ids.upload}`,
                source: ids.uploadButton,
                target: ids.upload,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
        ]
    },
    "steps": {
        nodes: [
            {
                id: ids.question,
                type: 'questionNode',
                data: { content: '', onContentUpdate: null, label: "parentNode" },
            },
            {
                id: ids.addButton,
                type: 'addButtonNode',
                data: { label: "addButtonNode", buttonType: "steps" },
            },
            {
                id: ids.steps,
                type: 'stepsNode',
                data: { content: '', onContentUpdate: null, label: "stepsNode" },
            },
            {
                id: ids.uploadButton,
                type: 'uploadButtonNode',
                data: { label: "uploadButtonNode", buttonType: "upload" },
            },
            {
                id: ids.upload,
                type: 'uploadNode',
                data: { label: "uploadNode" },
            }
        ],
        edges: [
            {
                id: `${ids.question}-${ids.addButton}`,
                source: ids.question,
                target: ids.addButton,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.addButton}-${ids.steps}`,
                source: ids.addButton,
                target: ids.steps,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.steps}-${ids.uploadButton}`,
                source: ids.steps,
                target: ids.uploadButton,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.uploadButton}-${ids.upload}`,
                source: ids.uploadButton,
                target: ids.upload,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
        ]
    },
    "yesorno": {
        nodes: [
            {
                id: ids.question,
                type: 'questionNode',
                data: { content: '', onContentUpdate: null, label: "parentNode" },
            },
            {
                id: ids.addButton,
                type: 'addButtonNode',
                data: { label: "addButtonNode", buttonType: "yesNo" },
            },
            {
                id: ids.yes,
                type: 'choiceNode',
                data: { label: 'yes', buttonType: "yes" },
            },
            {
                id: ids.no,
                type: 'choiceNode',
                data: { label: 'no', buttonType: "no" },
            }
        ],
        edges: [
            {
                id: `${ids.question}-${ids.addButton}`,
                source: ids.question,
                target: ids.addButton,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.addButton}-${ids.yes}`,
                source: ids.addButton,
                target: ids.yes,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
            {
                id: `${ids.addButton}-${ids.no}`,
                source: ids.addButton,
                target: ids.no,
                type: "smoothstep",
                animated: true,
                style: { stroke: "#CCCCCC", strokeWidth: 2 }
            },
        ]
    }
};

export const initialNodePositionsById: any = {
    "paragraph": {
        [ids.question]: { x: 0, y: 120 },
        [ids.addButton]: { x: 350, y: 181.6 },
        [ids.description]: { x: 600, y: 120 },
        [ids.uploadButton]: { x: 950, y: 181.6 },
        [ids.upload]: { x: 1200, y: 120 },
    },
    "steps": {
        [ids.question]: { x: 0, y: 120 },
        [ids.addButton]: { x: 350, y: 181.6 },
        [ids.steps]: { x: 600, y: 120 },
        [ids.uploadButton]: { x: 1050, y: 181.6 },
        [ids.upload]: { x: 1300, y: 120 },
    },
    "yesorno": {
        [ids.question]: { x: 0, y: 350 },
        [ids.addButton]: { x: 350, y: 411.6 },
        [ids.yes]: { x: 600, y: 114 },
        [ids.no]: { x: 600, y: 714 },
    }
};


export const getNodesAndEdgesBasedONDrag: any = (position: any, templateType: any) => {
    // Generate unique IDs for each node in the template
    const onDragNodesIds = {
        question: uuidv4(),
        addButton: uuidv4(),
        description: uuidv4(),
        uploadButton: uuidv4(),
        upload: uuidv4(),
        steps: uuidv4(),
        yes: uuidv4(),
        no: uuidv4(),
        link: uuidv4(),
    };

    const startNodePositionX = 50;
    const childNodePositionX = 350

    const positionsMap: Record<string, Record<string, { x: number, y: number }>> = {
        "paragraph": {
            [onDragNodesIds.question]: { x: startNodePositionX , y: position?.y + 120 },
            [onDragNodesIds.addButton]: { x: childNodePositionX + 150, y: position?.y + 181.6 },
            [onDragNodesIds.description]: { x: childNodePositionX + 500, y: position?.y + 120 },
            [onDragNodesIds.uploadButton]: { x: childNodePositionX + 900, y: position?.y + 181.6 },
            [onDragNodesIds.upload]: { x: childNodePositionX + 1200, y: position?.y + 120 },
        },
        "steps": {
            [onDragNodesIds.question]: { x: startNodePositionX, y: position?.y + 120 },
            [onDragNodesIds.addButton]: { x: childNodePositionX + 200, y: position?.y + 181.6 },
            [onDragNodesIds.steps]: { x: childNodePositionX + 600, y: position?.y + 120 },
            [onDragNodesIds.uploadButton]: { x: childNodePositionX + 1050, y: position?.y + 181.6 },
            [onDragNodesIds.upload]: { x: childNodePositionX + 1300, y: position?.y + 120 },
        },
        "yesorno": {
            [onDragNodesIds.question]: { x: startNodePositionX, y: position?.y },
            [onDragNodesIds.addButton]: { x: childNodePositionX + 200, y: position?.y + 61.6 },
            [onDragNodesIds.yes]: { x: childNodePositionX + 500, y: position?.y - 180 },
            [onDragNodesIds.no]: { x: childNodePositionX + 500, y: position?.y + 300 },
        },
        "link": {
            [onDragNodesIds.question]: { x: startNodePositionX, y: position?.y },
            [onDragNodesIds.addButton]: { x: childNodePositionX + 200, y: position?.y + 61.6 },
            [onDragNodesIds.link]: { x: childNodePositionX + 600, y: position?.y - 76.6 },
        },
    };
    

    const flowData = {
        "paragraph": {
            nodes: [
                {
                    id: onDragNodesIds.question,
                    type: 'questionNode',
                    position: { x: position?.x * 0, y: position?.y + 120 },
                    data: { content: '', onContentUpdate: null, label: "parentNode" },
                },
                {
                    id: onDragNodesIds.addButton,
                    type: 'addButtonNode',
                    position: { x: position?.x + 150, y: position?.y + 181.6 },
                    data: { label: "addButtonNode", buttonType: "paragraph" },
                },
                {
                    id: onDragNodesIds.description,
                    type: 'descriptionNode',
                    position: { x: position?.x + 500, y: position?.y + 120 },
                    data: { label: "descriptionNode", content: '' },
                },
                {
                    id: onDragNodesIds.uploadButton,
                    type: 'uploadButtonNode',
                    position: { x: position?.x + 900, y: position?.y + 185 },
                    data: { label: "uploadButtonNode", buttonType: "upload" },
                },
                {
                    id: onDragNodesIds.upload,
                    type: 'uploadNode',
                    position: { x: position?.x + 1200, y: position?.y + 120 },
                    data: { label: "uploadNode" },
                }
            ],
            edges: [
                {
                    id: `e-${onDragNodesIds.question}-${onDragNodesIds.addButton}`,
                    source: onDragNodesIds.question,
                    target: onDragNodesIds.addButton,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.addButton}-${onDragNodesIds.description}`,
                    source: onDragNodesIds.addButton,
                    target: onDragNodesIds.description,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.description}-${onDragNodesIds.uploadButton}`,
                    source: onDragNodesIds.description,
                    target: onDragNodesIds.uploadButton,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.uploadButton}-${onDragNodesIds.upload}`,
                    source: onDragNodesIds.uploadButton,
                    target: onDragNodesIds.upload,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
            ]
        },
        "steps": {
            nodes: [
                {
                    id: onDragNodesIds.question,
                    type: 'questionNode',
                    data: { content: '', onContentUpdate: null, label: "parentNode" },
                },
                {
                    id: onDragNodesIds.addButton,
                    type: 'addButtonNode',
                    data: { label: "addButtonNode", buttonType: "steps" },
                },
                {
                    id: onDragNodesIds.steps,
                    type: 'stepsNode',
                    data: { content: '', onContentUpdate: null, label: "stepsNode" },
                },
                {
                    id: onDragNodesIds.uploadButton,
                    type: 'uploadButtonNode',
                    data: { label: "uploadButtonNode", buttonType: "upload" },
                },
                {
                    id: onDragNodesIds.upload,
                    type: 'uploadNode',
                    data: { label: "uploadNode" },
                }
            ],
            edges: [
                {
                    id: `e-${onDragNodesIds.question}-${onDragNodesIds.addButton}`,
                    source: onDragNodesIds.question,
                    target: onDragNodesIds.addButton,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.addButton}-${onDragNodesIds.steps}`,
                    source: onDragNodesIds.addButton,
                    target: onDragNodesIds.steps,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.steps}-${onDragNodesIds.uploadButton}`,
                    source: onDragNodesIds.steps,
                    target: onDragNodesIds.uploadButton,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.uploadButton}-${onDragNodesIds.upload}`,
                    source: onDragNodesIds.uploadButton,
                    target: onDragNodesIds.upload,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
            ]
        },
        "yesorno": {
            nodes: [
                {
                    id: onDragNodesIds.question,
                    type: 'questionNode',
                    data: { content: '', onContentUpdate: null, label: "parentNode" },
                },
                {
                    id: onDragNodesIds.addButton,
                    type: 'addButtonNode',
                    data: { label: "addButtonNode", buttonType: "yesNo" },
                },
                {
                    id: onDragNodesIds.yes,
                    type: 'choiceNode',
                    data: { label: 'yes', buttonType: "yes" },
                },
                {
                    id: onDragNodesIds.no,
                    type: 'choiceNode',
                    data: { label: 'no', buttonType: "no" },
                }
            ],
            edges: [
                {
                    id: `e-${onDragNodesIds.question}-${onDragNodesIds.addButton}`,
                    source: onDragNodesIds.question,
                    target: onDragNodesIds.addButton,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.addButton}-${onDragNodesIds.yes}`,
                    source: onDragNodesIds.addButton,
                    target: onDragNodesIds.yes,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.addButton}-${onDragNodesIds.no}`,
                    source: onDragNodesIds.addButton,
                    target: onDragNodesIds.no,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
            ]
        },
        "link": {
            nodes: [
                {
                    id: onDragNodesIds.question,
                    type: 'questionNode',
                    data: { content: '', onContentUpdate: null, label: "parentNode" },
                },
                {
                    id: onDragNodesIds.addButton,
                    type: 'addButtonNode',
                    data: { label: "addButtonNode", buttonType: "link" },
                },
                {
                    id: onDragNodesIds.link,
                    type: 'linkNode',
                    data: { content: '', onContentUpdate: null, label: "linkNode" },
                },
            ],
            edges: [
                {
                    id: `e-${onDragNodesIds.question}-${onDragNodesIds.addButton}`,
                    source: onDragNodesIds.question,
                    target: onDragNodesIds.addButton,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
                {
                    id: `e-${onDragNodesIds.addButton}-${onDragNodesIds.link}`,
                    source: onDragNodesIds.addButton,
                    target: onDragNodesIds.link,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#CCCCCC", strokeWidth: 2 }
                },
            ]
        }
    };

    return { flowData, positionsMap };
};

export const transformNodesToQA = (nodes: any, edges: any) => {
    const output: Question[] = [];
    const processedNodes = new Set<string>();

    // Add null check and ensure we're working with an array
    if (!nodes || !Array.isArray(nodes)) {
        return [];
    }

    const questionNodes: any = nodes?.filter((node: any) =>
        node.type === "questionNode" && node.data.label === "parentNode"
    );

    for (const questionNode of questionNodes) {
        const question = processQuestionNode(questionNode, nodes, edges, processedNodes);
        if (question) {
            output?.push(question);
        }
    }

    function transformData(data: any) {
        return data.map((item: any) => {
            if (item?.answers?.length === 1) {
                // Remove `answers` and add `answer`
                const { answers, ...rest } = item;
                return {
                    ...rest,
                    answer: answers[0]
                };
            }
            return item; // Keep as is if `answers` has more than one element
        });
    }
    const finalOutput = transformData(output);
    function filterAnswers(data: any): any {
        const filterRecursive = (answers: any): any => {
            const hasYesNo = answers?.some((answer: any) => answer?.type === "yesNo");

            if (hasYesNo) {
                // Filter out descriptionNode and stepsNode in the current level
                return answers?.filter((answer: any) => answer?.type !== "descriptionNode" && answer?.type !== "stepsNode")
                    .map((answer: any) => {
                        // Recursively process nested_answer if present
                        if (answer?.nested_answer) {
                            return {
                                ...answer,
                                nested_answer: {
                                    question: answer?.nested_answer?.question,
                                    answers: filterRecursive(answer?.nested_answer?.answers),
                                },
                            };
                        }
                        return answer;
                    });
            }

            // No yesNo at this level, return all answers but still process nested_answer
            return answers?.map((answer: any) => {
                if (answer?.nested_answer) {
                    return {
                        ...answer,
                        nested_answer: {
                            question: answer?.nested_answer?.question,
                            answers: filterRecursive(answer?.nested_answer?.answers),
                        },
                    };
                }
                return answer;
            });
        };

        return data?.map((flow: any) => {
            if (flow?.answers) {
                return {
                    ...flow,
                    question: flow?.question,
                    answers: filterRecursive(flow?.answers),
                };
            }
            return flow;
        });
    }
    return filterAnswers(finalOutput);
};

function processQuestionNode(
    questionNode: any,
    nodes: any,
    edges: any,
    processedNodes: Set<string>
): Question | null {
    const question: Question = {
        question: questionNode?.data.content || "",
    };

    // Get all connected nodes through edges
    const connectedNodes = getAllConnectedNodes(questionNode?.id, nodes, edges);
    if (connectedNodes?.length > 0) {
        const answers = processConnectedNodes(connectedNodes, nodes, edges, processedNodes);
        if (answers?.length > 0) {
            question.answers = answers?.filter(answer => answer?.type === "yesNo" || answer?.type === "descriptionNode" || answer?.type === "stepsNode" || answer?.type === "linkNode");
        }
    }

    return question;
}

function getAllConnectedNodes(nodeId: string, nodes: any[], edges: any[]): any[] {
    const result = new Set<any>();
    const visited = new Set<string>();

    function traverse(currentId: string) {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const outgoingEdges = edges?.filter((edge) => edge?.source === currentId);
        for (const edge of outgoingEdges) {
            const targetNode = nodes.find((node) => node?.id === edge?.target);
            if (targetNode) {
                result.add(targetNode);
                traverse(targetNode?.id);
            }
        }
    }

    traverse(nodeId);
    return Array.from(result);
}

function processConnectedNodes(
    connectedNodes: any[],
    nodes: any,
    edges: any,
    processedNodes: Set<string>
): Answer[] {
    const answers: Answer[] = [];

    // Process choice nodes (yesNo answers)
    const choiceNodes = connectedNodes?.filter(node => node.type === "choiceNode");

    for (const node of choiceNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const yesNoAnswer: any = {
            type: "yesNo",
            value: node.data.label as "yes" | "no"
        };

        // Find connected qaNode
        const connectedToChoice = getAllConnectedNodes(node.id, nodes, edges);
        const qaNode = connectedToChoice.find(n => n.type === "qaNode");

        if (qaNode) {
            // Find connected question node
            const connectedToQA = getAllConnectedNodes(qaNode.id, nodes, edges);
            const nestedQuestionNode = connectedToQA.find(n => n.type === "questionNode");

            if (nestedQuestionNode) {
                // Get answers for nested question
                const nestedConnected = getAllConnectedNodes(nestedQuestionNode.id, nodes, edges);
                const nestedAnswers = processConnectedNodes(
                    nestedConnected,
                    nodes,
                    edges,
                    processedNodes
                );

                yesNoAnswer.nested_answer = {
                    question: nestedQuestionNode.data.content || "",
                    answers: nestedAnswers
                };
            }
        } else {
            // Check for addButtonNode
            const addButtonNode = connectedToChoice.find(n => n.type === "addButtonNode");

            if (addButtonNode) {
                // Find description and steps nodes connected to addButtonNode
                const addButtonConnected = getAllConnectedNodes(addButtonNode.id, nodes, edges);
                const nestedDescriptionNode = addButtonConnected.find(n => n.type === "descriptionNode");
                const nestedStepsNode = addButtonConnected.find(n => n.type === "stepsNode");
                const uploadNode = addButtonConnected.find(n => n.type === "uploadNode");
                const nestedLinkNode = addButtonConnected.find(n => n.type === "linkNode");

                if (nestedDescriptionNode) {
                    const descriptionAnswer: DescriptionAnswer = {
                        type: "descriptionNode",
                        value: nestedDescriptionNode.data.content || "",
                        is_upload: false
                    };

                    if (uploadNode) {
                        descriptionAnswer.is_upload = true;
                        descriptionAnswer.upload = uploadNode.data.content || "";
                    }

                    yesNoAnswer.nested_answer = {
                        answers: [descriptionAnswer]
                    };
                }

                if (nestedStepsNode) {
                    const stepsAnswer: StepsAnswer = {
                        type: "stepsNode",
                        value: nestedStepsNode.data.content || "",
                        is_upload: false
                    };

                    if (uploadNode) {
                        stepsAnswer.is_upload = true;
                        stepsAnswer.upload = uploadNode.data.content || "";
                    }

                    yesNoAnswer.nested_answer = {
                        answers: [stepsAnswer]
                    };
                }

                if (nestedLinkNode) {
                    const linkAnswer: LinkAnswer = {
                        type: "linkNode",
                        value: nestedLinkNode.data.content || "",
                        is_upload: false
                    };

                    if (uploadNode) {
                        linkAnswer.is_upload = true;
                        linkAnswer.upload = uploadNode.data.content || "";
                    }

                    yesNoAnswer.nested_answer = {
                        answers: [linkAnswer]
                    };
                }
            }
        }

        answers?.push(yesNoAnswer);
    }

    // Process description nodes
    const descriptionNodes = connectedNodes?.filter(node => node.type === "descriptionNode");
    for (const node of descriptionNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const descriptionAnswer: DescriptionAnswer = {
            type: "descriptionNode",
            value: node.data.content || "",
            is_upload: false
        };

        const connectedToNode = getAllConnectedNodes(node.id, nodes, edges);
        const uploadNode = connectedToNode.find(n => n.type === "uploadNode");

        if (uploadNode) {
            descriptionAnswer.is_upload = true;
            descriptionAnswer.upload = uploadNode.data.content || "";
        }

        answers?.push(descriptionAnswer);
    }

    // Process steps nodes
    const stepsNodes = connectedNodes?.filter(node => node.type === "stepsNode");
    for (const node of stepsNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const stepsAnswer: StepsAnswer = {
            type: "stepsNode",
            value: node.data.content || "",
            is_upload: false
        };

        const connectedToNode = getAllConnectedNodes(node.id, nodes, edges);
        const uploadNode = connectedToNode.find(n => n.type === "uploadNode");

        if (uploadNode) {
            stepsAnswer.is_upload = true;
            stepsAnswer.upload = uploadNode.data.content || "";
        }

        answers?.push(stepsAnswer);
    }

    // Process link nodes
    const linkNodes = connectedNodes?.filter(node => node.type === "linkNode");
    for (const node of linkNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const linkAnswer: LinkAnswer = {
            type: "linkNode",
            value: node.data.content || "",
            is_upload: false
        };

        const connectedToNode = getAllConnectedNodes(node.id, nodes, edges);
        const uploadNode = connectedToNode.find(n => n.type === "uploadNode");

        if (uploadNode) {
            linkAnswer.is_upload = true;
            linkAnswer.upload = uploadNode.data.content || "";
        }

        answers?.push(linkAnswer);
    }


    return answers;
}

type Answer = YesNoAnswer | DescriptionAnswer | StepsAnswer | LinkAnswer;

export const findPreviousNode = (currentNodeId: any, edges: any, nodes: any) => {
    // Find the edge where the target is the current node ID
    const previousEdge = edges?.find((edge: any) => edge?.target === currentNodeId);

    if (!previousEdge) {
        return null;
    }

    // Find the node where the ID matches the source of the found edge
    const previousNode = nodes?.find((node: any) => node?.id === previousEdge?.source);

    return previousNode || null;
};

export const findNextNode = (currentNodeId: any, edges: any, nodes: any) => {
    // Find the edge where the source is the current node ID
    const nextEdge = edges?.find((edge: any) => edge?.source === currentNodeId);

    if (!nextEdge) {
        return null;
    }

    // Find the node where the ID matches the target of the found edge
    const nextNode = nodes?.find((node: any) => node?.id === nextEdge?.target);

    return nextNode || null;
};

export const generateQANodesFromCSV_1 = (uploadData: any) => {
    const nodes: any = [];
    const edges: any = [];

    // Initial position for the first set of nodes
    // let currentX = 50;  // Starting X position
    // let currentY = 50;  // Starting Y position

    uploadData.forEach((item: any, index: any) => {
        // Generate unique IDs for each node
        const questionId = `question-${index}`;
        const addButtonId = `addButton-${index}`;
        const descriptionId = `description-${index}`;

        // Get second and third values
        const [secondValue, thirdValue] = getValuesByPosition(item, [1, 2]);


        // If there are previous nodes, calculate new position based on last node
        if (index > 0) {
            // const lastDescriptionNode = nodes[nodes.length - 1];
            // currentX = lastDescriptionNode?.position?.x + 100; // Horizontal offset from last node

            // Reset X and increment Y if we want to start a new row (optional)
            // if (index % 1 === 0) {  // Create new row every 3 sets
            //     currentX = 50;
            //     currentY = lastDescriptionNode?.position?.y + 300; // Vertical offset for new row
            // }
        }

        // Create question node
        const questionNode = {
            id: questionId,
            type: 'questionNode',
            data: {
                label: "parentNode",
                content: secondValue,
            }
        };

        // Create add button node - positioned below question node
        const addButtonNode = {
            id: addButtonId,
            type: 'addButtonNode',
            data: {
                label: "answerNode",
            }
        };

        // Create description node - positioned below add button node
        const descriptionNode = {
            id: descriptionId,
            type: 'descriptionNode',
            data: {
                label: "paragraph",
                content: thirdValue,
            }
        };

        // Add nodes to nodes array
        nodes.push(questionNode, addButtonNode, descriptionNode);

        // Create edges connecting the nodes
        const questionToAddButtonEdge = {
            id: `e${questionId}-${addButtonId}`,
            source: questionId,
            target: addButtonId,
            type: 'smoothstep',
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 }
        };

        const addButtonToDescriptionEdge = {
            id: `e${addButtonId}-${descriptionId}`,
            source: addButtonId,
            target: descriptionId,
            type: 'smoothstep',
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 }
        };

        // Add edges to edges array
        edges.push(questionToAddButtonEdge, addButtonToDescriptionEdge);
    });

    return { nodes, edges };
};

export const generateQANodesFromCSV = (uploadData: any) => {
    const nodes: any = [];
    const edges: any = [];
    const nodesPosition: Record<string, { x: number; y: number }> = {};

    // Initial position for the first set of nodes
    let currentX = 50;
    let currentY = 50;

    uploadData.forEach((item: any, index: any) => {
        // Generate unique IDs for each node
        const questionId = `question-${index}`;
        const addButtonId = `addButton-${index}`;
        const descriptionId = `description-${index}`;

        // Get second and third values
        const [secondValue, thirdValue] = getValuesByPosition(item, [1, 2]);

        // Calculate positions
        if (index > 0) {
            const lastNodeId = `description-${index - 1}`;
            currentX = nodesPosition[lastNodeId]?.x + 100;

            if (index % 1 === 0) {
                currentX = 50;
                currentY = nodesPosition[lastNodeId]?.y + 300;
            }
        }

        // Store positions in nodesPosition map
        nodesPosition[questionId] = { x: currentX, y: currentY };
        nodesPosition[addButtonId] = { x: currentX + 350, y: currentY + 61.6 };
        nodesPosition[descriptionId] = { x: currentX + 600, y: currentY };

        // Create question node
        const questionNode = {
            id: questionId,
            type: 'questionNode',
            data: {
                label: "parentNode",
                content: secondValue,
            }
        };

        // Create add button node
        const addButtonNode = {
            id: addButtonId,
            type: 'addButtonNode',
            data: {
                label: "answerNode",
            }
        };

        // Create description node
        const descriptionNode = {
            id: descriptionId,
            type: 'descriptionNode',
            data: {
                label: "paragraph",
                content: thirdValue,
            }
        };

        // Add nodes to nodes array
        nodes.push(questionNode, addButtonNode, descriptionNode);

        // Create edges connecting the nodes
        const questionToAddButtonEdge = {
            id: `e${questionId}-${addButtonId}`,
            source: questionId,
            target: addButtonId,
            type: 'smoothstep',
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 }
        };

        const addButtonToDescriptionEdge = {
            id: `e${addButtonId}-${descriptionId}`,
            source: addButtonId,
            target: descriptionId,
            type: 'smoothstep',
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 }
        };

        // Add edges to edges array
        edges.push(questionToAddButtonEdge, addButtonToDescriptionEdge);
    });

    return { nodes, edges, nodesPosition };
};

function getValuesByPosition<T extends object>(obj: T, positions: number[]): unknown[] {
    const values = Object.values(obj);
    return positions.map(pos => values[pos]);
}

//Name Conversion for Workbench node's profile image if not.
export const processString = (text: any) => {
    const words = text.split(' ');
    return words
        .slice(0, 2)         
        .map((word: any) => word[0])
        .join('');
}

// Helper function to highlight text
const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
        <span>
            {parts.map((part, index) =>
                part?.toLowerCase() === searchTerm?.toLowerCase() ? (
                    <span key={index} className="bg-yellow-200">{part}</span>
                ) : (
                    part
                )
            )}
        </span>
    );
};

const formatTitle = (node: ReactFlowNode, searchTerm: string): React.ReactNode => {
    const content = node.data.content || '';

    switch (node.type) {
        case 'questionNode':
            return (
                <div className="d-flex w-100 word-break">
                    Question: {highlightText(content, searchTerm)}
                </div>
            );
        case 'descriptionNode':
            return (
                <div className="d-flex">
                    Answer: {highlightText(content, searchTerm)}
                </div>
            );
        case 'uploadNode':
            return (
                <div className="d-flex">
                    Upload: {content}
                </div>
            );
        default:
            return content;
    }
};

function findNextAllowedNode(
    currentNodeId: string,
    edges: ReactFlowEdge[],
    nodesMap: Map<string, ReactFlowNode>,
    allowedTypes: Set<string>
): ReactFlowNode | null {
    let nextNodeId = currentNodeId;

    while (true) {
        const edge = edges.find(e => e.source === nextNodeId);
        if (!edge) return null;

        const nextNode = nodesMap.get(edge.target);
        if (!nextNode) return null;

        if (allowedTypes.has(nextNode.type)) {
            return nextNode;
        }

        nextNodeId = edge.target;
    }
}

// Function to check if a node or its children match the search term
function nodeMatchesSearch(node: ReactFlowNode, searchTerm: string): boolean {
    if (!searchTerm) return true;

    const content = (node?.data?.content || '').toLowerCase();
    return content.includes(searchTerm?.toLowerCase());
}

export function convertToQuestionAnswerTree(
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[],
    searchTerm: string = ''
): DataNode[] {
    if (!nodes || !edges) return [];

    const allowedTypes = new Set(['questionNode', 'descriptionNode', 'stepsNode', 'linkNode', 'uploadNode']);
    const nodesMap = new Map(nodes.map(node => [node.id, node]));

    // Find root nodes (questions with no incoming edges)
    const hasIncomingEdge = new Set(edges.map(edge => edge.target));
    const rootNodes = nodes.filter(node =>
        node.type === 'questionNode' && !hasIncomingEdge.has(node.id)
    );

    function buildTreeNode(node: ReactFlowNode): DataNode | null {
        // Check if current node matches search
        const matches = nodeMatchesSearch(node, searchTerm);

        // Get next allowed node
        const nextAllowedNode = findNextAllowedNode(node.id, edges, nodesMap, allowedTypes);

        // Build children recursively
        const childNode = nextAllowedNode ? buildTreeNode(nextAllowedNode) : null;

        // If there's no search term, or if this node/child matches, include it
        if (!searchTerm || matches || childNode) {
            return {
                key: node.id,
                title: formatTitle(node, searchTerm),
                children: childNode ? [childNode] : []
            };
        }

        return null;
    }

    // Build and filter the tree
    const result = rootNodes
        .map(node => buildTreeNode(node))
        .filter((node): node is DataNode => node !== null);

    return result;
}
